<template>
  <aside class="image-face-panel" @click.stop @dblclick.stop>
    <header>
      <div>
        <small>{{ $t('people_current_assignment') }}</small>
        <strong>{{ currentLabel }}</strong>
      </div>
      <button type="button" :aria-label="$t('cancel')" @click="$emit('close')">
        <i class="bi bi-x-lg" />
      </button>
    </header>

    <template v-if="creatingDraft">
      <b-form-input
        ref="draftName"
        v-model="draftName"
        class="image-face-draft-name"
        :placeholder="$t('people_name_placeholder')"
        @keydown.enter="createDraft"
      />
      <div class="image-face-panel-actions">
        <b-button variant="primary" :disabled="busy || !draftName.trim()" @click="createDraft">
          {{ $t('people_create') }}
        </b-button>
        <b-button variant="outline-light" :disabled="busy" @click="cancelDraft">
          {{ $t('cancel') }}
        </b-button>
      </div>
    </template>

    <template v-else>
      <PersonAssignmentSelect
        class="image-face-target"
        :value="face.person_id"
        :confirmed-targets="confirmedTargets"
        :draft-targets="draftTargets"
        :disabled="busy"
        @input="selectTarget"
      />
      <div class="image-face-panel-actions">
        <b-button variant="outline-light" :disabled="busy" @click="$emit('create-unconfirmed')">
          {{ $t('people_move_to_unconfirmed') }}
        </b-button>
        <b-button
          variant="outline-danger"
          :disabled="busy || face.assignment_state === 'ignored'"
          @click="$emit('ignore')"
        >
          {{ $t('people_ignore_one') }}
        </b-button>
      </div>
    </template>

    <div v-if="busy" class="image-face-panel-status">
      <b-spinner small />
      <span>{{ $t('status_wip') }}</span>
    </div>
    <b-alert v-if="error" show variant="danger" class="image-face-panel-error">
      {{ error }}
    </b-alert>
  </aside>
</template>

<script>
import PersonAssignmentSelect from '../configuration/people/PersonAssignmentSelect.vue'

export default {
  components: { PersonAssignmentSelect },
  props: {
    face: { type: Object, required: true },
    targets: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  data() {
    return {
      creatingDraft: false,
      draftName: '',
    }
  },
  computed: {
    confirmedTargets() {
      return this.targets.filter(person => person.status === 'confirmed')
    },
    draftTargets() {
      return this.targets.filter(person => person.status === 'candidate' && person.name?.trim())
    },
    currentLabel() {
      const name = this.face.person?.name?.trim()
      if (name) return name
      if (this.face.assignment_state === 'ignored') return this.$t('people_ignored')
      if (this.face.assignment_state === 'review') return this.$t('people_needs_review')
      if (this.face.person?.status === 'candidate') return this.$t('people_unconfirmed')
      return this.$t('people_ungrouped')
    },
  },
  methods: {
    selectTarget(targetId) {
      if (targetId === 'create-draft') {
        this.creatingDraft = true
        this.draftName = ''
        this.$nextTick(() => this.$refs.draftName?.focus())
        return
      }
      const target = this.targets.find(person => person.id === targetId)
      if (target) this.$emit('assign', target)
    },
    createDraft() {
      const name = this.draftName.trim()
      if (!name || this.busy) return
      this.$emit('create-draft', name)
      this.creatingDraft = false
      this.draftName = ''
    },
    cancelDraft() {
      this.creatingDraft = false
      this.draftName = ''
    },
  },
}
</script>

<style scoped>
.image-face-panel {
  position: fixed;
  z-index: 4;
  right: 24px;
  bottom: 24px;
  width: min(360px, calc(100vw - 48px));
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 8px;
  background: rgba(var(--dark-bg-rgb), .96);
  box-shadow: 0 12px 32px rgba(0, 0, 0, .5);
}

.image-face-panel.on-left {
  right: auto;
  left: 24px;
}

.image-face-panel header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.image-face-panel header div {
  min-width: 0;
}

.image-face-panel header small,
.image-face-panel header strong {
  display: block;
}

.image-face-panel header small {
  color: var(--faded-text-color);
}

.image-face-panel header strong {
  overflow: hidden;
  font-size: 1.15rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-face-panel header button {
  padding: 4px;
  border: 0;
  background: transparent;
  color: var(--faded-text-color);
  cursor: pointer;
}

.image-face-target,
.image-face-draft-name {
  width: 100%;
}

.image-face-panel-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.image-face-panel-actions > * {
  flex: 1 1 0;
}

.image-face-panel-status {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
  color: var(--faded-text-color);
}

.image-face-panel-error {
  margin: 12px 0 0;
}

@media (max-width: 600px) {
  .image-face-panel {
    right: 12px;
    left: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
  }
}
</style>
